"use client";

import {
  $createNodeSelection,
  $setSelection,
  type LexicalEditor,
  type NodeKey,
} from "lexical";
import { Pencil } from "lucide-react";
import type * as React from "react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { OPEN_LINK_DIALOG_COMMAND } from "../commands";

export default function ButtonLinkComponent({
  url,
  text,
  target,
  variant,
  size,
  isFullWidth,
  buttonLinkNodeKey,
  editor,
  isSelected,
  isEditable,
}: {
  url: string;
  text: string;
  target: string | null;
  variant: "primary" | "secondary";
  size: "default" | "sm" | "lg";
  isFullWidth: boolean;
  buttonLinkNodeKey: NodeKey;
  editor: LexicalEditor;
  isSelected: boolean;
  isEditable: boolean;
}) {
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.update(() => {
      const selection = $createNodeSelection();
      selection.add(buttonLinkNodeKey);
      $setSelection(selection);
    });
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.update(() => {
      const selection = $createNodeSelection();
      selection.add(buttonLinkNodeKey);
      $setSelection(selection);

      const data = {
        url,
        text,
        target,
        variant,
        size,
        isFullWidth,
        nodeKey: buttonLinkNodeKey,
      };
      editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, data);
    });
  };

  return (
    <div
      className={cn(
        "relative group",
        isFullWidth ? "block w-full" : "inline-block",
      )}
    >
      <Button
        asChild
        variant={variant === "primary" ? "default" : "outline"}
        size={size}
        className={cn("no-underline my-0 cursor-pointer", {
          "w-full justify-center": isFullWidth,
          "ring-2 ring-primary ring-offset-2": isSelected,
        })}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <a
          href={url}
          target={target || undefined}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {text}
        </a>
      </Button>

      {/* Hover Edit Button */}
      {isEditable && (
        <div className="absolute -top-3 -right-3 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <Button
            size="icon"
            variant="secondary"
            className="h-7 w-7 rounded-full shadow-md border"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Select the node first and then open the dialog
              editor.update(() => {
                const selection = $createNodeSelection();
                selection.add(buttonLinkNodeKey);
                $setSelection(selection);

                const data = {
                  url,
                  text,
                  target,
                  variant,
                  size,
                  isFullWidth,
                  nodeKey: buttonLinkNodeKey,
                };
                editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, data);
              });
            }}
            title="Redigera länk"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
